import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { EventCommercial } from '../../hooks/event-comparison'
import { SERIES_BASE, SERIES_COMPARE } from './styles'

const INK = '#212121'
const MUTED = '#555555'
const FAINT = '#B0B0B0'
const BORDER = '#EAECF0'
const TRACK = '#EEF1F6'

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 24px 0 12px;
`
const Kicker = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${FAINT};
  text-transform: uppercase;
`
const Name = styled.h2`
  font-size: 15px;
  font-weight: 680;
  color: ${INK};
  margin: 0;
`
const Note = styled.span`
  margin-left: auto;
  font-size: 11.5px;
  color: ${FAINT};
`
const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`
const Card = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  padding: 16px;
`
const CardTitle = styled.div`
  font-size: 14.5px;
  font-weight: 660;
  color: ${INK};
`
const CardSub = styled.div`
  font-size: 11.5px;
  color: ${FAINT};
  margin: 2px 0 14px;
`
const Empty = styled.div`
  font-size: 12.5px;
  color: ${FAINT};
  padding: 8px 0;
`

/* barras horizontais base × comparado */
const HRow = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
  & + & {
    border-top: 1px solid #f2f4f7;
  }
`
const HLabel = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${INK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const HBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const HTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const HFill = styled.div<{ $w: number; $c: string }>`
  height: 9px;
  border-radius: 5px;
  min-width: 2px;
  background: ${(p) => p.$c};
  width: ${(p) => Math.max(p.$w, 0)}%;
`
const HPct = styled.span<{ $c: string }>`
  font-size: 12px;
  font-weight: 660;
  color: ${(p) => p.$c};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
const LegendMini = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  font-size: 11.5px;
  color: ${MUTED};
  margin-top: 12px;
`
const LDot = styled.span<{ $c: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$c};
  margin-right: 6px;
`

/* meia × inteira empilhado */
const StackRow = styled.div`
  padding: 10px 0;
  & + & {
    border-top: 1px solid #f2f4f7;
  }
`
const StackTop = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12.5px;
  margin-bottom: 7px;
`
const StackBar = styled.div`
  display: flex;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
  background: ${TRACK};
`

/* heatmap */
const HeatWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`
const HeatTitle = styled.div`
  font-size: 12.5px;
  font-weight: 620;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`
const HeatGrid = styled.div`
  display: grid;
  grid-template-columns: 34px repeat(7, 1fr);
  gap: 4px;
`
const ColH = styled.div`
  font-size: 9.5px;
  color: ${FAINT};
  text-align: center;
`
const RowH = styled.div`
  font-size: 9px;
  color: ${FAINT};
  display: flex;
  align-items: center;
`
const Cell = styled.div<{ $bg: string }>`
  aspect-ratio: 1 / 0.62;
  border-radius: 4px;
  background: ${(p) => p.$bg};
`

const BAND_LABEL: Record<string, string> = {
  madrugada: 'Madr.',
  manha: 'Manhã',
  tarde: 'Tarde',
  noite: 'Noite',
}

interface AlignedRow {
  key: string
  label: string
  a: number
  b: number
}
function alignByKey(
  aItems: { key: string; label: string; pct: number }[],
  bItems: { key: string; label: string; pct: number }[],
  order: string[]
): AlignedRow[] {
  const aMap = new Map(aItems.map((i) => [i.key, i]))
  const bMap = new Map(bItems.map((i) => [i.key, i]))
  const keys = order.filter((k) => aMap.has(k) || bMap.has(k))
  return keys.map((key) => ({
    key,
    label: aMap.get(key)?.label || bMap.get(key)?.label || key,
    a: aMap.get(key)?.pct || 0,
    b: bMap.get(key)?.pct || 0,
  }))
}

const HBarList: FC<{ rows: AlignedRow[]; baseName: string; compareName: string }> = ({
  rows,
  baseName,
  compareName,
}) => {
  const max = Math.max(1, ...rows.flatMap((r) => [r.a, r.b]))
  if (!rows.length) return <Empty>Sem dados na janela.</Empty>
  return (
    <div>
      {rows.map((r) => (
        <HRow key={r.key}>
          <HLabel title={r.label}>{r.label}</HLabel>
          <HBars>
            <HTrack>
              <HFill $w={(r.a / max) * 100} $c={SERIES_BASE} />
              <HPct $c={SERIES_BASE}>{r.a.toFixed(0)}%</HPct>
            </HTrack>
            <HTrack>
              <HFill $w={(r.b / max) * 100} $c={SERIES_COMPARE} />
              <HPct $c={SERIES_COMPARE}>{r.b.toFixed(0)}%</HPct>
            </HTrack>
          </HBars>
        </HRow>
      ))}
      <LegendMini>
        <span><LDot $c={SERIES_BASE} />{baseName}</span>
        <span><LDot $c={SERIES_COMPARE} />{compareName}</span>
      </LegendMini>
    </div>
  )
}

const MeiaInteira: FC<{ name: string; c: EventCommercial; color: string }> = ({ name, c, color }) => {
  const inteira = c.ticketTypes.find((t) => t.tipo === 'Inteira')?.pct || 0
  const meia = c.ticketTypes.find((t) => t.tipo === 'Meia')?.pct || 0
  const softColor = color === SERIES_BASE ? 'rgba(176,2,11,0.42)' : 'rgba(33,33,33,0.40)'
  return (
    <StackRow>
      <StackTop>
        <span style={{ fontWeight: 620, color: INK }}>{name}</span>
        <span style={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
          Inteira {inteira.toFixed(0)}% · Meia {meia.toFixed(0)}%
        </span>
      </StackTop>
      <StackBar>
        <div style={{ width: `${inteira}%`, background: color }} />
        <div style={{ width: `${meia}%`, background: softColor }} />
      </StackBar>
    </StackRow>
  )
}

const HeatCard: FC<{ name: string; c: EventCommercial; rgb: string }> = ({ name, c, rgb }) => {
  const hm = c.heatmap
  if (!hm || !hm.cells?.length) return <Empty>Sem dados de horário.</Empty>
  const max = Math.max(1, ...hm.cells.flat())
  return (
    <div>
      <HeatTitle><span style={{ width: 9, height: 9, borderRadius: 3, background: `rgb(${rgb})` }} />{name}</HeatTitle>
      <HeatGrid>
        <div />
        {hm.days.map((d) => (
          <ColH key={d}>{d}</ColH>
        ))}
        {hm.cells.map((row, bi) => (
          <React.Fragment key={bi}>
            <RowH>{BAND_LABEL[hm.bands[bi]] || hm.bands[bi]}</RowH>
            {row.map((v, di) => (
              <Cell key={di} $bg={`rgba(${rgb},${(0.08 + (v / max) * 0.92).toFixed(2)})`} title={`${v} ingressos`} />
            ))}
          </React.Fragment>
        ))}
      </HeatGrid>
    </div>
  )
}

interface Props {
  baseName: string
  compareName: string
  baseCommercial: EventCommercial
  compareCommercial: EventCommercial
}

const CHANNEL_ORDER = ['web', 'pdv', 'comissario', 'cortesia']
const LEAD_ORDER = ['60+', '31-60', '16-30', '0-15']

/** Seções COMERCIAL (canais, meia×inteira) + COMPORTAMENTO (antecedência, heatmap). OP-3415. */
const CommercialBehavior: FC<Props> = ({ baseName, compareName, baseCommercial, compareCommercial }) => {
  const channelRows = useMemo(
    () => alignByKey(baseCommercial.channels, compareCommercial.channels, CHANNEL_ORDER),
    [baseCommercial.channels, compareCommercial.channels]
  )
  const leadRows = useMemo(
    () =>
      alignByKey(
        baseCommercial.leadTime.map((l) => ({ key: l.bucket, label: l.label, pct: l.pct })),
        compareCommercial.leadTime.map((l) => ({ key: l.bucket, label: l.label, pct: l.pct })),
        LEAD_ORDER
      ),
    [baseCommercial.leadTime, compareCommercial.leadTime]
  )

  return (
    <>
      <Grid2>
        <Card>
          <CardTitle>Canais de venda</CardTitle>
          <CardSub>% dos ingressos · Web / PDV / Comissário / Cortesia</CardSub>
          <HBarList rows={channelRows} baseName={baseName} compareName={compareName} />
        </Card>

        <Card>
          <CardTitle>Meia × Inteira</CardTitle>
          <CardSub>Proporção de meia-entrada por evento</CardSub>
          {baseCommercial.ticketTypes.length === 0 && compareCommercial.ticketTypes.length === 0 ? (
            <Empty>Sem dados de tipo de ingresso na janela.</Empty>
          ) : (
            <>
              <MeiaInteira name={baseName} c={baseCommercial} color={SERIES_BASE} />
              <MeiaInteira name={compareName} c={compareCommercial} color={SERIES_COMPARE} />
              <LegendMini>
                <span><LDot $c={SERIES_BASE} />Inteira (cheio)</span>
                <span><LDot $c={'#c7cfe8'} />Meia (claro)</span>
              </LegendMini>
            </>
          )}
        </Card>
      </Grid2>

      <SectionHead>
        <Kicker>Comportamento</Kicker>
        <Name>Antecedência e horário de compra</Name>
        <Note>Quando o público comprou, na janela</Note>
      </SectionHead>

      <Grid2>
        <Card>
          <CardTitle>Antecedência de compra</CardTitle>
          <CardSub>Dias entre a compra e a data do evento</CardSub>
          <HBarList rows={leadRows} baseName={baseName} compareName={compareName} />
        </Card>

        <Card>
          <CardTitle>Mapa de calor de compra</CardTitle>
          <CardSub>Dia da semana × período do dia · intensidade = volume</CardSub>
          <HeatWrap>
            <HeatCard name={baseName} c={baseCommercial} rgb="176,2,11" />
            <HeatCard name={compareName} c={compareCommercial} rgb="33,33,33" />
          </HeatWrap>
        </Card>
      </Grid2>
    </>
  )
}

export default CommercialBehavior
